/**
 * ConnectorDetailPanel — the tabbed detail body for a connector,
 * presentation-agnostic.
 *
 * The shared inner: brand glyph + title + status + header actions, a pencil tab
 * strip (rendered per the connector manifest), and a scrollable content region.
 * It fills its container (``h-full``), so the surrounding chrome controls size.
 * Today its sole consumer is ``ConnectorDetailSection`` (the URL-addressable
 * in-page detail takeover — DB/ERP + spreadsheet); it stays chrome-agnostic so a
 * future drawer/modal host could reuse the same body.
 *
 * Generic + presentational: the caller supplies the tab list ({id, label,
 * content}) and the header actions — no per-capability branching here.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import * as React from "react";

import { cn } from "@ando/ui";

export interface ConnectorDrawerTab {
  readonly id: string;
  readonly label: React.ReactNode;
  readonly content: React.ReactNode;
}

export interface ConnectorDetailPanelProps {
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  /** Status element (e.g. ``<ConnectorStatus variant="inline">``). */
  readonly status?: React.ReactNode;
  /** Leading brand glyph. */
  readonly icon?: React.ReactNode;
  readonly tabs: readonly ConnectorDrawerTab[];
  /** Initial tab (uncontrolled). Defaults to the first tab. */
  readonly initialTabId?: string;
  /** Controlled active tab (optional — pair with ``onTabChange``). */
  readonly activeTabId?: string;
  readonly onTabChange?: (id: string) => void;
  /** Header action buttons (verify / discover / edit …). */
  readonly headerActions?: React.ReactNode;
}

/**
 * The shared inner body: header (glyph/title/status/actions) + tab strip +
 * scrollable content. Fills its parent (``h-full``), so the surrounding chrome
 * controls size.
 */
export const ConnectorDetailPanel: React.FC<ConnectorDetailPanelProps> = ({
  title,
  subtitle,
  status,
  icon,
  tabs,
  initialTabId,
  activeTabId,
  onTabChange,
  headerActions,
}) => {
  const { i18n } = useLingui();
  const firstId = initialTabId ?? tabs[0]?.id;
  const [internalTab, setInternalTab] = React.useState<string | undefined>(firstId);
  const currentId = activeTabId ?? internalTab ?? firstId;
  const activeTab = tabs.find((t) => t.id === currentId) ?? tabs[0];

  const selectTab = (id: string) => {
    if (activeTabId === undefined) setInternalTab(id);
    onTabChange?.(id);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 border-hairline border-b px-5 py-4">
        <div className="flex items-start gap-3">
          {icon ? <span className="mt-0.5 shrink-0">{icon}</span> : null}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h2 className="truncate font-semibold text-[15px] text-ink-1">{title}</h2>
              {status}
            </div>
            {subtitle ? (
              <p className="mt-0.5 truncate text-[12.5px] text-ink-3">{subtitle}</p>
            ) : null}
          </div>
          {headerActions ? (
            <div className="flex shrink-0 items-center gap-1.5">{headerActions}</div>
          ) : null}
        </div>

        {tabs.length > 1 ? (
          <div
            role="tablist"
            aria-label={i18n._(msg`Sezioni del connettore`)}
            className="-mb-4 mt-3.5 flex flex-wrap gap-x-1 gap-y-0"
          >
            {tabs.map((t) => {
              const on = t.id === activeTab?.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => selectTab(t.id)}
                  className={cn(
                    "border-b-2 px-2.5 pb-2.5 font-medium text-[12.5px] transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                    on
                      ? "border-ando-orange font-semibold text-ink-1"
                      : "border-transparent text-ink-3 hover:text-ink-1",
                  )}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </header>

      <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {activeTab?.content}
      </div>
    </div>
  );
};
ConnectorDetailPanel.displayName = "ConnectorDetailPanel";
